/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as Highcharts from 'highcharts';

import template from './line-chart.component.html';

import {ADN_CHART_COUNT_TYPES} from '../options/chart-options';
import {DEVICE_ORDER_ID} from './perf-total-component';

@Component({
  standalone: false,
  selector: 'adn-line-chart',
  template,
  host: {
    class: 'chart',
    style: 'display: block; width: 100%;'
  }
})
export class LineChartComponent implements OnChanges, OnDestroy {
  @Input() stats: any;
  @Input() statsKey: string;
  @Input() chartType: string;
  @Input() title: string;
  @Input() keyed: string;
  @Input() orderBy: string;
  @Input() eventHook: any;
  @Input() countType: any;

  haveStats = false;
  chartHeight = 175;
  error: any = null;
  noData: any = null;

  @ViewChild('chartContainer')
  set chartContainer(chartContainer: ElementRef<HTMLElement>) {
    this.chartContainerRef = chartContainer;
    this.renderChart();
  }

  private chart: Highcharts.Chart | null = null;
  private chartContainerRef: ElementRef<HTMLElement> | null = null;
  private chartOptions: any = null;

  constructor(private readonly translate: TranslateService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.eventHook) {
      this.eventHook.redraw = () => this.updateChartState();
    }

    if (changes.stats || changes.statsKey || changes.countType || changes.orderBy || changes.keyed) {
      this.updateChartState();
    }
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  private updateChartState(): void {
    if (!this.stats?.totals?.[this.statsKey]) {
      this.haveStats = false;
      this.chartOptions = null;
      this.destroyChart();
      return;
    }

    const totals = this.stats.totals;
    let series: any[] = _.map(totals[this.statsKey], (d, k) => ({
      name: this.keyed ? (this.translate.instant(this.keyed + '.' + k) || k) : k,
      id: k,
      type: 'line'
    }));

    if (this.orderBy === 'VALUE') {
      series = _.slice(_.orderBy(_.map(totals[this.statsKey], (d, k) => ({
        name: this.keyed ? (this.translate.instant(this.keyed + '.' + k) || k) : k,
        id: k,
        type: 'line',
        orderBy: d
      })), ['orderBy'], 'desc'), 0, 40);
    } else if (this.orderBy === 'MOBILE_INFO') {
      series = _.orderBy(_.map(series, (entry) => ({
        ...entry,
        orderId: DEVICE_ORDER_ID[entry.id as keyof typeof DEVICE_ORDER_ID] || 1000
      })), 'orderId');
    }

    const keyedChunks = _.keyBy(this.stats.chunks, 'chunkStart');
    _.forEach(series, (s) => {
      let runningTotal = 0;
      s.data = _.map(this.stats.chunkRange, (statsDate: string) => {
        let dataPoint = null;
        if (keyedChunks[statsDate]) {
          dataPoint = _.get(keyedChunks, [statsDate, this.statsKey, s.id]);
        }
        const value = dataPoint || 0;
        runningTotal += value;
        return {
          x: moment(statsDate).valueOf(),
          y: this.countType === ADN_CHART_COUNT_TYPES.cumulative ? runningTotal : value
        };
      });
    });

    this.chartOptions = {
      chart: {
        type: 'line',
        zoomType: 'x',
        height: this.chartHeight,
        width: null
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false
          },
          turboThreshold: 2000
        }
      },
      tooltip: {
        shared: true
      },
      yAxis: {
        title: {text: null},
        min: 0,
        allowDecimals: false
      },
      xAxis: {
        type: 'datetime',
        title: {
          enabled: false,
          text: 'Date and Time'
        }
      },
      legend: {
        layout: 'vertical',
        verticalAlign: 'top',
        width: 110,
        align: 'right'
      },
      credits: {
        enabled: false
      },
      accessibility: {
        enabled: false
      },
      title: {
        text: null
      },
      series
    };

    this.haveStats = true;
    this.renderChart();
  }

  private renderChart(): void {
    if (!this.chartContainerRef?.nativeElement || !this.chartOptions || this.noData) {
      return;
    }

    this.destroyChart();
    this.chart = Highcharts.chart(this.chartContainerRef.nativeElement, this.chartOptions);
    requestAnimationFrame(() => {
      this.chart?.reflow();
    });
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
